import React from "react";
import ModalUI from "../legacy/Modal";
import { QueueModalActions, QueueModalTitle } from "../Queue/QueueModalParts";
import { ImpactedAppointment } from "../../services/availability/types";
import { formatDateKey, impactedPatientName } from "../../utils/availability";

type AvailabilityImpactModalProps = {
  appointments: ImpactedAppointment[];
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const SHORT_DATE: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short", year: "numeric" };

/**
 * Confirmation d'un changement de disponibilité qui touche des RDV futurs.
 * Les RDV ne sont ni déplacés ni annulés : ils restent à reprogrammer.
 */
const AvailabilityImpactModal: React.FC<AvailabilityImpactModalProps> = ({
  appointments,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  const count = appointments.length;
  const plural = count > 1 ? "s" : "";

  return (
    <ModalUI
      isOpen
      onClose={() => {
        if (!isSaving) onCancel();
      }}
      styles={{ width: "min(560px, 92vw)", padding: 0 }}
    >
      <div className="flex flex-col">
        <div className="space-y-4 px-6 pt-6">
          <QueueModalTitle
            eyebrow="Rendez-vous concernés"
            title={`${count} rendez-vous hors disponibilité`}
          />
          <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <i className="bx bx-error-circle mt-0.5 text-base" aria-hidden />
            <span>
              {count > 1
                ? "Ces rendez-vous restent en place et seront à reprogrammer."
                : "Ce rendez-vous reste en place et sera à reprogrammer."}{" "}
              Aucun patient n'est prévenu automatiquement.
            </span>
          </p>
          <div className="max-h-72 overflow-y-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="px-4 py-2.5">Patient</th>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Heure</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment) => (
                  <tr key={appointment.consultationId} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium text-slate-800">{impactedPatientName(appointment)}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {formatDateKey(appointment.appointmentDate, SHORT_DATE)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 tabular-nums">
                      {appointment.appointmentTime.slice(0, 5)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <QueueModalActions
          onCancel={onCancel}
          onConfirm={onConfirm}
          confirmLabel={`Confirmer malgré le${plural} RDV`}
          confirmIcon="bx-check"
          confirmDisabled={isSaving}
          isLoading={isSaving}
        />
      </div>
    </ModalUI>
  );
};

export default AvailabilityImpactModal;
